import { reviseElevateLearnerCopy } from "@/data/elevateLearnerCopy";
import { applyImplementationPath } from "@/data/elevateImplementationPath";
import { ROCKLINE_SHOWCASE_EXAMPLES, ROCKLINE_SHOWCASE_BY_CORE_ID } from "@/data/s2pElevateShowcaseExamples";
import { applyElevateFacilitation, applyElevateFacilitationTiming } from "@/data/elevateFacilitation";
import { applyElevatePromptFormat, applyElevateShowcasePromptFormat } from "@/data/elevatePromptFormat";
import { applyAgentSetupSession, replaceElevateSessionFour } from "@/data/elevateAgentSetup";
import { addElevateHomework } from "@/data/elevateHomework";
import { applyRocklineSessionFive } from "@/data/elevateRocklineSessionFive";
import {
  S2P_ELEVATE_BASE_CURRICULUM,
  createS2PElevateProgram,
  type S2PElevateCurriculum,
  type S2PElevateShowcase,
  type S2PElevateSlide,
} from "@/data/s2pElevateAI";

const ROCKLINE_SOURCE_IDS = {
  tools: "rockline-chat.tools",
  learningNeeds: "rockline-chat.learning-needs",
  workflows: "rockline-chat.workflows",
} as const;

const deliveryPaths = (): S2PElevateShowcase["support"] => [
  {
    provider: "Current process",
    status: "Current workflow",
    detail: "Record the current result and review effort for comparison with the Copilot pilot.",
    tone: "yes",
  },
  {
    provider: "Microsoft Copilot",
    status: "Approval dependent",
    detail: "Use the Rockline-approved Copilot account and permitted evidence. Confirm the license, work sources, and enabled agents with the owner.",
    tone: "yes",
  },
  {
    provider: "Copilot agent or automation",
    status: "Access dependent",
    detail: "Use only when the required license, permissions, systems, and owner are confirmed.",
    tone: "partial",
  },
  {
    provider: "Human decision",
    status: "Required",
    detail: "Keep approval with the accountable person whenever the output can affect people, production, safety, quality, or money.",
    tone: "yes",
  },
];

const ROCKLINE_SHOWCASE: S2PElevateShowcase[] = [
  {
    id: "line-scheduling",
    platform: "Microsoft Copilot",
    category: "Planning and scheduling · Transformation opportunity",
    title: "Compare production schedules without losing the operating constraints",
    lead: "Give AI the orders, line requirements, changeover rules, and decision boundary so it can surface options rather than silently choose one.",
    bullets: [
      "Compare candidate schedules against the same documented constraints.",
      "Show the tradeoffs behind throughput, changeovers, due dates, and exceptions.",
      "Stop at a planner-reviewed recommendation before anything changes in production.",
    ],
    agentic: "The workflow gathers approved inputs, follows clear rules, explains exceptions, and pauses for the planner's decision.",
    implication: "Start with decision support. Add optimization or system updates only after people trust the recommendation.",
    applications: [
      { function: "Planning", use: "Compare two-line schedules against orders, capacity, and line rules." },
      { function: "Operations", use: "Surface constraints and changeover risks before the daily plan is approved." },
      { function: "Materials", use: "Flag schedule options that conflict with material availability." },
      { function: "Quality", use: "Preserve quality holds and required checks as non-negotiable constraints." },
      { function: "Leadership", use: "Review the tradeoffs behind the recommendation instead of receiving a black-box answer." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "forecast-inventory",
    platform: "Microsoft Copilot",
    category: "Forecasting and inventory · Decision support",
    title: "Turn forecast and inventory data into an exception brief",
    lead: "Focus attention on meaningful changes, missing assumptions, and decisions instead of manually scanning every row.",
    bullets: [
      "Compare forecast, inventory, and order signals using the same review criteria.",
      "Identify exceptions and trace each observation back to its source data.",
      "Draft questions for the planner instead of inventing an explanation for a gap.",
    ],
    agentic: "The workflow reads the authorized data, applies a repeated exception test, prepares a sourced brief, and escalates missing or contradictory inputs.",
    implication: "The first win is not an autonomous forecast. It is a faster, more consistent review that helps the owner investigate the right exceptions.",
    applications: [
      { function: "Forecasting", use: "Explain material changes and identify assumptions that need confirmation." },
      { function: "Inventory", use: "Flag potential shortages, excess, and mismatches for review." },
      { function: "Supply Chain", use: "Connect demand changes to likely downstream decisions." },
      { function: "Finance", use: "Prepare commentary on changes with links back to the source details." },
      { function: "Leadership", use: "Receive a decision-ready brief instead of an unfiltered workbook." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "logistics-transportation",
    platform: "Microsoft Copilot",
    category: "Logistics and transportation · Analysis",
    title: "Find the transportation patterns worth investigating",
    lead: "Use AI to summarize recurring delays, cost changes, and constraint patterns while the logistics owner keeps the operating decision.",
    bullets: [
      "Group recurring exceptions by route, carrier, facility, or reason code.",
      "Separate observations from suggested explanations.",
      "Prepare the next questions and actions for a human-led review.",
    ],
    agentic: "The workflow combines multiple approved inputs, applies a repeated analysis pattern, and produces an exception queue with source links and review gates.",
    implication: "Start by making exceptions easy to see and review. Do not let AI make an unsupervised routing decision.",
    applications: [
      { function: "Logistics", use: "Summarize repeat delays and cost exceptions by lane or carrier." },
      { function: "Transportation", use: "Compare options against documented service and operating constraints." },
      { function: "Customer Service", use: "Draft a sourced explanation of affected orders for review." },
      { function: "Finance", use: "Highlight cost movements that need investigation." },
      { function: "Operations", use: "Connect recurring exceptions to the process that owns the next action." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "quality-supplier",
    platform: "Microsoft Copilot",
    category: "Quality and supplier management · Reporting",
    title: "Build a weekly supplier-quality exception brief",
    lead: "Turn approved quality data into a consistent summary of changes, source details, open questions, and required human decisions.",
    bullets: [
      "Identify meaningful changes without hiding the underlying counts or sources.",
      "Separate confirmed facts, suspected causes, and missing information.",
      "Stop for the quality owner's review before distributing conclusions.",
    ],
    agentic: "The workflow retrieves the current inputs, runs the same checks, drafts the brief, and pauses when data is missing or a quality judgement is required.",
    implication: "A governed quality brief is a strong first build: repeated, measurable, useful, and safe to validate before broader automation.",
    applications: [
      { function: "Quality", use: "Draft a sourced weekly exception brief for review." },
      { function: "Supplier Management", use: "Prepare open questions and follow-up actions by supplier." },
      { function: "Engineering", use: "Connect observed changes to the source details that need technical review." },
      { function: "Operations", use: "Highlight process areas that need investigation without assigning cause." },
      { function: "Leadership", use: "See material changes, confidence, and unresolved questions in one view." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "safety-procedures",
    platform: "Microsoft Copilot",
    category: "Safety, EHS, and procedures · Content workflow",
    title: "Draft training and procedures from approved source material",
    lead: "Use the current policy, standard, and subject-matter input to prepare a draft while preserving the safety owner's approval.",
    bullets: [
      "Cite the approved materials used for each important requirement.",
      "Flag conflicts, gaps, or language that needs an expert decision.",
      "Generate the requested format without presenting the draft as approved policy.",
    ],
    agentic: "The workflow gathers authorized sources, follows a standard content pattern, checks for missing inputs, and stops before approval or publication.",
    implication: "AI can reduce drafting and formatting time while the safety or EHS leader keeps the technical and policy judgment.",
    applications: [
      { function: "EHS", use: "Draft training material from current approved requirements." },
      { function: "Safety", use: "Prepare a procedure draft with unresolved questions clearly marked." },
      { function: "HR", use: "Translate approved procedures into role-relevant onboarding material." },
      { function: "Operations", use: "Create a first draft of a work instruction from expert input." },
      { function: "Quality", use: "Check that the draft points back to the controlling source." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "sourcing-contracts",
    platform: "Microsoft Copilot",
    category: "Sourcing and contracts · Knowledge work",
    title: "Compare contract language and prepare the negotiation brief",
    lead: "Summarize differences, locate controlling language, and prepare questions without allowing AI to make the commercial or legal decision.",
    bullets: [
      "Compare terms against the approved template or review checklist.",
      "Quote or link to the clause behind every material observation.",
      "Escalate ambiguous, missing, or high-risk language to the right reviewer.",
    ],
    agentic: "The workflow follows a repeatable review sequence, uses approved reference material, and stops when a judgement belongs to sourcing, finance, or counsel.",
    implication: "The useful output is a faster review packet and clearer questions, not an AI-approved agreement.",
    applications: [
      { function: "Sourcing", use: "Prepare a comparison and negotiation question list." },
      { function: "Finance", use: "Surface commercial terms that require financial review." },
      { function: "Legal", use: "Receive a structured issue list with clause references." },
      { function: "Project Management", use: "Track unresolved decisions, owners, and due dates." },
      { function: "Leadership", use: "Review the material tradeoffs without reading an unstructured comparison." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "analytics-reporting",
    platform: "Microsoft Copilot",
    category: "Analytics and reporting · Repeated deliverable",
    title: "Turn analysis into a sourced, decision-ready report",
    lead: "Use AI to help with formulas, summaries, charts, and narrative while keeping the calculation and source visible.",
    bullets: [
      "State the question, metric definitions, source ranges, and comparison period.",
      "Require the output to distinguish calculation, observation, and interpretation.",
      "Validate formulas and numbers before sharing the narrative.",
    ],
    agentic: "The workflow repeats a defined analysis and reporting process, checks required inputs, and routes uncertain conclusions to the data owner.",
    implication: "The path from Excel, Power BI, or Databricks to a useful report becomes repeatable without making the model the source of truth.",
    applications: [
      { function: "Analytics", use: "Draft a narrative that links each claim back to the calculation." },
      { function: "Power BI", use: "Prepare questions, measure explanations, and release notes for review." },
      { function: "Data", use: "Document assumptions and unresolved data-quality issues." },
      { function: "Finance", use: "Create a variance brief with confirmed figures and open questions." },
      { function: "Leadership", use: "Receive the decision and source details together." },
    ],
    support: deliveryPaths(),
  },
  {
    id: "governed-knowledge",
    platform: "Microsoft Copilot",
    category: "Documents and knowledge · Respect existing permissions",
    title: "Help people find the right document without flattening permissions",
    lead: "Answer from approved policies, SOPs, and resource guides while respecting the access rules already attached to the source material.",
    bullets: [
      "Search only the sources the current user is authorized to access.",
      "Return the controlling document and date with the answer.",
      "Escalate missing, conflicting, or out-of-date guidance to the content owner.",
    ],
    agentic: "The workflow checks permissions, uses approved sources, cites the answer, and stops when the source is missing or a human needs to decide.",
    implication: "A knowledge assistant is useful only when source ownership, access, freshness, and escalation remain visible.",
    applications: [
      { function: "HR", use: "Answer from role-appropriate policies and resource guides." },
      { function: "Operations", use: "Locate the current SOP and point to the controlling section." },
      { function: "Quality", use: "Surface the approved procedure without exposing restricted material." },
      { function: "Managers", use: "Find team guidance while preserving manager-only sources." },
      { function: "Associates", use: "Receive a direct answer with the source they can open." },
    ],
    support: deliveryPaths(),
  },
].map((item) => ({
  ...item,
  example: ROCKLINE_SHOWCASE_EXAMPLES[item.id],
  sourceIds: [ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.tools],
}));

const slideOverrides: Record<string, Partial<S2PElevateSlide>> = {
  welcome: {
    eyebrow: "Serve2Perform ELEVATE · Rockline · Five-session workshop",
    title: "AI Tools & Workflows for Rockline Leaders",
    lead: "Build one Rockline Chief of Staff Skill in Copilot across five decisions: write the prompt, choose the Job and tool, control the first run, then improve it with evidence.",
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.workflows],
  },
  "session-1": {
    title: "Complex Prompting Made Easy",
    lead: "Set the approved standing context in Copilot once. Then give the Chief of Staff a clear outcome, approved sources, boundaries, and a Rockline review standard.",
    bullets: [
      "Define what the Chief of Staff should know about your role and priorities.",
      "Set the Rockline data and authority boundary.",
      "Compare a vague request with a reusable Skill.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "showcase-debrief": {
    title: "Why is this the best supported tool for the Job?",
    questions: [
      "Which Job requirement ruled out a lighter approved tool?",
      "Which approved Rockline source or required action drove the choice?",
      "Why might the Chief of Staff and AI Watcher need different tool paths?",
      "What evidence from the first run would make you change the Tool Decision?",
    ],
    note: "The Rockline Chief of Staff remains the learner artifact. The AI Watcher provides a second pattern for comparison.",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "three-layers": {
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "atomic-reasoning-protocol": {
    lead: "Give the Rockline Chief of Staff a repeatable method for solving problems and explaining its reasoning.",
    sourceUrl: undefined,
    sourceLabel: undefined,
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "get-unstuck": {
    lead: "Ask for a plain-language explanation before deciding what to change or try next.",
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "session-2": {
    title: "Pick the Right Work for AI",
    lead: "Use real Rockline calendar evidence to find recurring work where AI can produce a useful, reviewable result.",
    bullets: [
      "Find recurring work in one real week.",
      "Compare value, feasibility, and risk.",
      "Add the strongest first Job to the Rockline Chief of Staff Skill.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "session-2-job-examples": {
    lead: "Choose an example that resembles something on your own Rockline calendar. Describe what you do before, during, and after it.",
    sections: [
      { title: "Prepare for a meeting", body: "Turn approved shift notes into a handoff brief covering unresolved issues and questions for the supervisor." },
      { title: "Follow up on commitments", body: "Turn operations-review notes into a draft follow-up list for the meeting owner to verify." },
      { title: "Review what changed", body: "Compare approved supplier-quality reports and draft an exception summary for the quality manager." },
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "session-2-choose-job-prompt": {
    lead: "Use the result to update your Rockline Chief of Staff Skill. Name the Rockline reviewer who still makes the decision.",
    quote: "When the weekly supplier-quality report is ready, prepare a sourced exception brief so the quality manager can decide what needs investigation.",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "privacy-team-rule": {
    eyebrow: "Rockline Chief of Staff setup · Data and authority",
    title: "One boundary covers company data, access, and action",
    lead: "Use the same authority ladder throughout the program.",
    sections: [
      { label: "Default", title: "Read", body: "Inspect and link to approved Rockline sources without changing anything." },
      { label: "Reversible", title: "Draft", body: "Prepare work for a named Rockline reviewer." },
      { label: "Controlled", title: "Change", body: "Edit only within an approved scope and recovery path." },
      { label: "Consequential", title: "Send or publish", body: "Require a named person to approve external actions or decisions affecting production, quality, safety, people, contracts, or money." },
    ],
    bullets: [
      "Use only the approved Rockline account and sources you are authorized to access.",
      "Start the first run at Read or Draft.",
    ],
    promptLabel: "Rockline Chief of Staff boundary",
    prompt: "For this Rockline Chief of Staff Skill, use only [APPROVED SOURCES]. You may [READ] and [DRAFT]. Ask [ROCKLINE REVIEWER] before you [CHANGE, SEND, OR PUBLISH]. Never access [SENSITIVE INFORMATION]. Record the sources, output, and human decision so the run can be reviewed.",
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "connections-and-tools": {
    title: "Confirm the source and permission before you connect it",
    lead: "A useful workflow knows which files, reports, or systems are trusted and who may access them.",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "outcome-first-prompt-example": {
    title: "How might we prepare a ranked daily priority brief within read-only access and Rockline approval boundaries, based on my approved Outlook calendar, commitments, and current tasks?",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "workflow-idea-sprint": {
    lead: "Use the Rockline Showcase pathways and your own recurring work to choose a quick win now and carry one transformation opportunity into Session 3.",
    note: "Build now: one low-risk A idea. Bring next: one B opportunity and one or two C ideas to rank using what you know.",
    sourceUrl: undefined,
    sourceLabel: undefined,
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "qa-1": {
    title: "What changed when the prompt became a Reusable Skill?",
    questions: [
      "Which part of the Skill helped most: standing context, sources, boundaries, or the review standard?",
      "Where did the improved prompt reduce ambiguity or rework?",
      "Which Rockline decision must remain with a person?",
      "Which software control enforces access, and why does saving a Skill not grant permission?",
    ],
    note: "Save the Skill only after you can explain why the improved result is better.",
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "session-3": {
    eyebrow: "Session 3 of 5 · Pick the Best AI Tool For the Job",
    title: "Pick the Best AI Tool For the Job",
    lead: "Use the selected Rockline Job to choose the approved Copilot experience, sources, and authority that can finish it.",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "locked-in-job": {
    sections: [
      {
        title: "Job",
        body: "When the weekly supplier-quality data is available, prepare a sourced exception brief for the quality manager's review.",
      },
      {
        title: "RICE",
        body: "Score reach, impact, confidence, and effort using current Rockline information before deciding whether this should be first.",
      },
      {
        title: "Time steal",
        body: "Measure the repeated collection, comparison, formatting, and clarification time in the current process.",
      },
      {
        title: "Sandbox",
        body: "Use approved sample or low-stakes data; draft for review and do not distribute conclusions automatically.",
      },
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "session-4": {
    title: "Control AI Agents",
    lead: "Turn the selected Job and Tool Decision into one supervised run with explicit access, authority, stop rules, and Rockline review.",
    bullets: [
      "Set the human direction and agent boundary.",
      "Use only the Rockline sources and actions the first run needs.",
      "Review the evidence and record the human decision.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "session-3-prompt-example": {
    title: "The Rockline AI Watcher decision brief",
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.workflows],
  },
  "session-4-calendar-audit": {
    title: "Your Rockline calendar can reveal the first Chief of Staff Jobs",
    promptLabel: "Rockline Chief of Staff calendar audit",
    prompt: "Review one week of my approved Rockline calendar. Find 3 to 5 recurring Jobs a Chief of Staff agent could prepare, coordinate, follow up, or review.\n\nFor each, give evidence, trigger, approved input, reviewable output, Rockline reviewer, and risk. Favor frequent, valuable, low-risk work. If access is unavailable, ask for an approved redacted screenshot. Do not invent examples.",
    sourceUrl: undefined,
    sourceLabel: undefined,
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "token-budget-controls": {
    sourceUrl: undefined,
    sourceLabel: undefined,
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "workflow-example": {
    eyebrow: "Example · Supplier-quality brief",
    title: "The workflow stops before the quality decision",
    bullets: [
      "Load the approved weekly supplier-quality inputs and confirm the reporting period.",
      "Identify material changes and link every observation to its source.",
      "Separate confirmed facts, suspected causes, and questions that need investigation.",
      "Stop at Draft for Review if data is missing, conflicting, stale, or outside the approved scope.",
    ],
    note: "Done: the quality manager can verify the sources, understand the changes, and decide the next action without reconstructing the analysis.",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "connection-routes": {
    title: "Use the simplest approved way to reach each source",
    lead: "Start with the files and Microsoft 365 tools you already have. Add connectors, agents, or automation only after ownership and access are confirmed.",
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.workflows],
  },
  "session-5": {
    title: "Improve AI Agents",
    lead: "Use the Reviewed Agent Run to measure one result, make one evidence-backed change, and compare the next run.",
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "agent-team-boundary": {
    title: "One agent handles one result inside a Rockline-owned boundary",
    lead: "The agent prepares reviewable work. A named Rockline person controls access and consequential decisions.",
    bullets: [
      "One result: Keep the first run tied to one Rockline Job and its definition of done.",
      "Approved context: Give the agent only the sources, rules, and examples it needs.",
      "Human control: A named Rockline person reviews the result and approves consequences.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "chief-of-staff-job": {
    title: "The Rockline Chief of Staff needs a tool that can prepare a ranked brief",
    lead: "Compare the Job's approved sources, actions, and review point before choosing the tool.",
    sections: [
      {
        label: "Cadence",
        title: "Run when priorities can change",
        body: "Choose a morning, workday, or explicit manual run. Start manually before you schedule anything.",
      },
      {
        label: "Sources",
        title: "Read only approved systems",
        body: "Name the exact Outlook or calendar, Teams messages, SharePoint or OneDrive files, tasks, and commitments it may read.",
      },
      {
        label: "Outcome",
        title: "Produce one ranked brief",
        body: "Show what matters now, what is blocked, and what can wait. Link every priority to its evidence.",
      },
      {
        label: "Human",
        title: "Keep the Rockline owner accountable",
        body: "A person approves, sends, edits records, and makes consequential decisions.",
      },
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "chief-of-staff-decision-rules": {
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "chief-of-staff-operating-brief": {
    title: "The Reusable Skill becomes a read-only Rockline Chief of Staff",
    lead: "Add the Job from Session 2 and Tool Decision from Session 3, run it manually, then review every priority against its approved source.",
    promptLabel: "Rockline Chief of Staff Reusable Skill",
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.workflows, ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "session-3-do-this-first": {
    eyebrow: "15-minute build",
    title: "Run and review the smallest Rockline Chief of Staff agent",
    lead: "Use the Job added to the Skill in Session 2 and the approved Tool Decision from Session 3.",
    promptLabel: "Prepare the first Rockline Chief of Staff run",
    prompt: "Use my Rockline Chief of Staff Skill to prepare one reviewable run for [JOB].\n\nConfirm the trigger, approved sources, tool path, definition of done, authority level, reviewer, and stop rules before you begin. Start at Read or Draft. Stop when a source, permission, instruction, or expected result is unclear. Record the sources, result, and questions for the reviewer.",
    sourceUrl: "/ai-mastermind/8-19-26#15",
    sourceLabel: "Open the source slide",
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "clarifying-questions": {
    title: "A useful workflow begins with specific clarifying answers",
    bullets: [
      "Answer every question with a concrete fact, constraint, source, or owner.",
      "If you do not know, say: research the method, propose an approach, then ask me to confirm.",
      "Do not let the workflow guess about data, permissions, approval gates, or the finish line.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds],
  },
  "where-data-lives": {
    title: "If you do not know where the data lives, finding the owner is part of the work",
    bullets: [
      "Identify the approved source and the person or function that owns it.",
      "Confirm the participant is authorized to use the source for this Job.",
      "If the route is unclear, stop and ask the data owner or IT instead of improvising around the boundary.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.tools, ROCKLINE_SOURCE_IDS.workflows],
  },
  "stop-rules": {
    title: "Tell the workflow when it must stop instead of guessing",
    promptLabel: "Rockline stop rules",
    prompt: "STOP immediately and notify me if:\n- A required source is missing, stale, empty, or outside the approved reporting period.\n- The data classification, permission, owner, or destination is unclear.\n- The output could change production, quality, safety, people, contracts, or money without the named human approval.\n- Two approved sources conflict or a required input is null.\n- You are not certain you understood the instruction. Ask one clarifying question instead of guessing.\n\nDo not publish, distribute, update a system, or make the final decision unless the workflow explicitly names that approved action.",
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "feedback-and-schedule": {
    title: "Each reviewed result changes the next Rockline run",
    sections: [
      {
        label: "Run",
        title: "Complete the recurring Job",
        body: "Use the approved inputs, boundaries, and definition of done.",
      },
      {
        label: "Measure",
        title: "Read the result",
        body: "Compare the metric and visible evidence with the baseline and definition of done.",
      },
      {
        label: "Human",
        title: "Decide what should change",
        body: "Choose one bounded change while the named Rockline person keeps consequential judgment.",
      },
      {
        label: "Improve",
        title: "Update the Skill",
        body: "Save the reviewed correction or example, then compare the next run with the same measure.",
      },
    ],
    note: "Repeat manually until the source, output, stop rules, and review hold up. Approve a schedule only after repeated evidence supports it.",
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "demo-prompt": {
    eyebrow: "Example · Exact prompt",
    title: "The prompt names the outcome, sources, and decision boundary",
    lead: "This uses the supplier-quality workflow already mapped: approved weekly inputs, a defined reporting period, linked sources, and a quality-manager review.",
    promptLabel: "Rockline example wording",
    prompt: "How might we create a reusable AI workflow that prepares the weekly supplier-quality exception brief from the approved reporting inputs, identifies material changes with links to the approved inputs, separates facts from questions, and stops for the quality manager's review before distribution?",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "demo-clarifications": {
    eyebrow: "Example · Ask before building",
    title: "Answer 4 questions before the build",
    questions: [
      "Which approved sources contain the weekly supplier-quality inputs?",
      "What reporting period, thresholds, and comparison rules define a material change?",
      "Who owns each source and who is allowed to receive the brief?",
      "Which conditions require the workflow to stop for the quality manager?",
    ],
    note: "Start the build only after the source, thresholds, audience, and stop conditions are clear.",
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "demo-results": {
    eyebrow: "Example · Human on the loop",
    title: "The first run reaches the review gate with the source links intact",
    bullets: [
      "The workflow uses only the approved reporting inputs for the stated period.",
      "Every material observation points back to its source.",
      "Suspected causes and missing information remain questions, not conclusions.",
      "The quality manager retains the interpretation, distribution, and next-action decisions.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "part-four-close": {
    title: "Make the Rockline loop boring and reliable",
    bullets: [
      "The agent owns one recurring result and uses only approved Rockline sources.",
      "The metric, Definition of Done, cadence, and destination are explicit.",
      "The system preserves the approval gate for production, safety, quality, people, contracts, and money.",
      "The named Rockline owner reviews the evidence and improves the next run.",
    ],
    warning: "One good demo is not permission to share unrestricted context, expand access, or add unattended automation.",
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "qa-4": {
    title: "What changed in the next Rockline run because of evidence?",
    questions: [
      "Did the result improve, or did the agent only create more activity?",
      "Which source, instruction, example, or stop rule changed?",
      "What evidence made the next run easier to trust and review?",
      "Did accepted quality, review effort, retries, or cost change the Tool Decision? Record proceed, revise, or stop before scheduling or expanding access.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  complete: {
    title: "The Rockline Chief of Staff is ready for the next run",
    lead: "Run it again, review the evidence, and improve the Skill. Approve a schedule only when the result holds up.",
    bullets: [
      "Reusable Skill: complex instructions made clear enough to reuse.",
      "Selected Job: recurring Rockline work supported by real evidence.",
      "Tool Decision: the lightest approved path that can finish the Job.",
      "Reviewed Agent Run: one controlled run with the human decision recorded.",
      "Measurable Loop: one result that changes the next run.",
    ],
    sourceIds: [ROCKLINE_SOURCE_IDS.learningNeeds, ROCKLINE_SOURCE_IDS.workflows],
  },
  "use-case-library-workflows": {
    title: "Rockline use cases for decision support",
    lead: "Revisit the Rockline examples from Sessions 3 through 5 and choose a pattern for your next Job.",
    showcaseIds: ["line-scheduling", "forecast-inventory", "logistics-transportation", "quality-supplier"],
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
  "use-case-library-scale": {
    title: "Rockline use cases for repeatable work",
    lead: "Revisit the reporting, training, sourcing, and knowledge examples from the workshop.",
    showcaseIds: ["safety-procedures", "sourcing-contracts", "analytics-reporting", "governed-knowledge"],
    sourceIds: [ROCKLINE_SOURCE_IDS.workflows],
  },
};

const mergeAudienceSources = (slide: S2PElevateSlide, override?: Partial<S2PElevateSlide>): S2PElevateSlide => ({
  ...slide,
  ...override,
  sourceIds: [...new Set([...(slide.sourceIds ?? []), ...(override?.sourceIds ?? [])])],
});

const customizedSlides = S2P_ELEVATE_BASE_CURRICULUM.slides
  .filter((slide) => !["ai-swot-library", "ai-workflow-pilot", "level-fit-huddle"].includes(slide.id))
  .map((slide) => {
    if (slide.kind === "showcase-example" && slide.showcaseId) {
      const showcaseId = ROCKLINE_SHOWCASE_BY_CORE_ID[slide.showcaseId];
      const item = ROCKLINE_SHOWCASE.find((candidate) => candidate.id === showcaseId)!;
      return mergeAudienceSources(slide, {
        id: `showcase-${showcaseId}`,
        showcaseId,
        title: item.example!.title,
        sourceIds: item.sourceIds,
      });
    }
    return applyImplementationPath(mergeAudienceSources(slide, slideOverrides[slide.id]), true);
  });

const rocklineCurriculum: S2PElevateCurriculum = reviseElevateLearnerCopy({
  course: {
    ...S2P_ELEVATE_BASE_CURRICULUM.course,
    title: "AI Tools & Workflows for Rockline Leaders",
    subtitle: "Make complex prompting easy. Pick the right Rockline work and tool, control the first agent run, then improve it with evidence.",
    guideHref: undefined,
  },
  sessions: [
    {
      id: 1,
      label: "Complex Prompting Made Easy",
      title: "Complex Prompting Made Easy",
      minutes: 75,
      coreMinutes: 45,
      exerciseMinutes: 15,
      discussionMinutes: 15,
      outcome: "A reusable Chief of Staff Skill that turns a vague request into a grounded Rockline result a person can review.",
    },
    {
      id: 2,
      label: "Pick the Right Work for AI",
      title: "Pick the Right Work for AI",
      minutes: 75,
      coreMinutes: 45,
      exerciseMinutes: 15,
      discussionMinutes: 15,
      outcome: "One recurring, valuable, low-risk Job selected from real Rockline calendar evidence and added to the Chief of Staff Skill.",
    },
    {
      id: 3,
      label: "Pick the Best AI Tool For the Job",
      title: "Pick the Best AI Tool For the Job",
      minutes: 75,
      coreMinutes: 45,
      exerciseMinutes: 15,
      discussionMinutes: 15,
      outcome: "A supported Tool Decision for the selected Job, including the model, access, authority, and evidence that would change the choice.",
    },
    {
      id: 4,
      label: "Control AI Agents",
      title: "Control AI Agents",
      minutes: 75,
      coreMinutes: 45,
      exerciseMinutes: 15,
      discussionMinutes: 15,
      outcome: "A Reviewed Agent Run that uses approved sources, stays within its authority, stops when required, and records the human decision.",
    },
    {
      id: 5,
      label: "Improve AI Agents",
      title: "Improve AI Agents",
      minutes: 75,
      coreMinutes: 45,
      exerciseMinutes: 15,
      discussionMinutes: 15,
      outcome: "A Measurable Loop that compares one visible result, records the decision, and changes the next run.",
    },
  ].map((session) => ({
    ...session,
    id: session.id as 1 | 2 | 3 | 4 | 5,
    teachingPlan: S2P_ELEVATE_BASE_CURRICULUM.sessions.find((core) => core.id === session.id)?.teachingPlan?.map((part) =>
      session.id === 3 && part.title === "Pilot framing"
        ? { ...part, title: "Compare the Chief of Staff and AI Watcher paths" }
        : part),
  })).map(applyElevateFacilitationTiming).map((session) => applyAgentSetupSession(session, true)),
  checkpoints: [],
  showcase: ROCKLINE_SHOWCASE.map(applyElevateShowcasePromptFormat),
  slides: addElevateHomework(replaceElevateSessionFour(customizedSlides.map((slide) => applyElevatePromptFormat(applyElevateFacilitation(slide, true), true)), true), true),
});

export const S2P_ELEVATE_ROCKLINE_CURRICULUM: S2PElevateCurriculum = {
  ...rocklineCurriculum,
  slides: rocklineCurriculum.slides.map(applyRocklineSessionFive),
};

export const S2P_ELEVATE_ROCKLINE_PROGRAM = createS2PElevateProgram(
  S2P_ELEVATE_ROCKLINE_CURRICULUM,
);
